import localizeCurrency from '@app-studio-web/ui/helpers/localizeCurrency'
import localizeDate from '@app-studio-web/ui/helpers/localizeDate'
import { type Plan, type StripePrice } from '@app-studio-web/ui/types'
import { type PaymentIntent } from '@stripe/stripe-js'
import { addDays, addMonths, addWeeks, addYears } from 'date-fns'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { twMerge } from 'tailwind-merge'

interface Props {
  className?: string
  fullPrice: number | undefined
  fullPriceCurrency: string | undefined
  paymentIntent: PaymentIntent | undefined
  plan: Plan
  stripePrice: StripePrice | undefined
  stripePriceFull: StripePrice | undefined
  stripePriceIntro: StripePrice | undefined | null
  trialPeriodDays?: number
}

function capitalize(value: string | undefined) {
  if (!value) return ''

  return value.charAt(0).toUpperCase() + value.slice(1)
}

/**
 * li-funnel-redesign v1 — the order summary above the payment form.
 *
 * Same information and the same translation keys as the shared summary, laid
 * out as label/value rows instead of stacked paragraphs. The shared one reads
 * as prose, which buries the one number the buyer is looking for; here the
 * amount due sits in its own tinted row at the bottom where the eye lands.
 */
export default function OrderSummary({
  className,
  fullPrice,
  fullPriceCurrency,
  paymentIntent,
  plan,
  stripePrice,
  stripePriceFull,
  stripePriceIntro,
  trialPeriodDays,
}: Props) {
  const { t } = useTranslation()

  const isOneTime = plan === 'one-time'
  const interval = stripePrice?.recurring?.interval
  const intervalCount = stripePrice?.recurring?.interval_count ?? 1

  const renewalDate = useMemo(() => {
    if (isOneTime) return null

    const now = new Date()

    if (trialPeriodDays) return addDays(now, trialPeriodDays)
    if (interval === 'week') return addWeeks(now, intervalCount)
    if (interval === 'month') return addMonths(now, intervalCount)
    if (interval === 'year') return addYears(now, intervalCount)

    return addWeeks(now, 1)
  }, [interval, intervalCount, isOneTime, trialPeriodDays])

  const duration = trialPeriodDays
    ? t('pages.checkout.payment.orderSummary.durationTrial', {
        count: trialPeriodDays,
      })
    : isOneTime
      ? t('pages.checkout.payment.orderSummary.durationOneTime')
      : t(
          `pages.checkout.payment.orderSummary.duration${capitalize(interval) || 'Week'}`,
          { count: intervalCount },
        )

  return (
    <div className={twMerge('animate-fade-up space-y-2.5', className)}>
      <h2 className="text-hw-ink text-[17px] font-bold">
        {t('pages.checkout.payment.orderSummary.heading')}
      </h2>

      <div className="hw-card divide-hw-line divide-y overflow-hidden">
        <div className="flex items-start justify-between gap-4 px-4 py-3">
          <p className="text-hw-muted text-[13px] font-medium">
            {t('pages.checkout.payment.orderSummary.durationHeadline')}
          </p>

          <p className="text-hw-ink text-right text-sm font-semibold">
            {duration}
          </p>
        </div>

        <div className="flex items-start justify-between gap-4 px-4 py-3">
          <p className="text-hw-muted text-[13px] font-medium">
            {t('pages.checkout.payment.orderSummary.renewalHeadline')}
          </p>

          <div className="text-right">
            {isOneTime ? (
              <p className="text-hw-ink text-sm font-semibold">
                {t('pages.checkout.payment.orderSummary.noRenewal')}
              </p>
            ) : (
              <>
                {renewalDate && (
                  <p className="text-hw-ink text-sm font-semibold">
                    {localizeDate(renewalDate)}
                  </p>
                )}

                <p className="text-hw-muted mt-0.5 text-xs leading-snug">
                  {trialPeriodDays
                    ? t('pages.checkout.payment.orderSummary.trialRenewal', {
                        price: localizeCurrency({
                          amountInCents: fullPrice,
                          currency: fullPriceCurrency,
                        }),
                      })
                    : t(
                        'pages.checkout.payment.orderSummary.renewalDisclaimer',
                      )}
                </p>
              </>
            )}
          </div>
        </div>

        {/*
          The amount due. Tinted rather than just bold, because on a payment
          form this is the only row the buyer actually checks before committing.
        */}
        {trialPeriodDays ? (
          <p className="bg-hw-primary-soft text-hw-primary px-4 py-3.5 text-[15px] font-bold">
            {t('pages.checkout.payment.orderSummary.dueTodayFree', {
              price: localizeCurrency({
                amountInCents: 0,
                currency: fullPriceCurrency,
              }),
            })}
          </p>
        ) : (
          paymentIntent && (
            <div className="bg-hw-primary-soft px-4 py-3.5">
              {isOneTime ? (
                <p className="text-hw-primary text-[15px] font-bold">
                  {t('pages.checkout.payment.oneTimePayment', {
                    price: localizeCurrency({
                      amountInCents: paymentIntent.amount,
                      currency: paymentIntent.currency,
                    }),
                  })}
                </p>
              ) : stripePriceIntro ? (
                <>
                  <p className="text-hw-muted text-xs line-through">
                    {t(
                      `pages.checkout.payment.this${capitalize(stripePriceFull?.recurring?.interval)}`,
                      {
                        count: stripePriceFull?.recurring?.interval_count,
                        price: localizeCurrency({
                          amountInCents: fullPrice,
                          currency: fullPriceCurrency,
                        }),
                      },
                    )}
                  </p>

                  <p className="text-hw-primary text-[15px] font-bold">
                    {t(
                      `pages.checkout.payment.first${capitalize(stripePriceIntro?.recurring?.interval)}SpecialOffer`,
                      {
                        count: stripePriceIntro?.recurring?.interval_count,
                        price: localizeCurrency({
                          amountInCents: paymentIntent.amount,
                          currency: paymentIntent.currency,
                        }),
                      },
                    )}
                  </p>
                </>
              ) : (
                <p className="text-hw-primary text-[15px] font-bold">
                  {t(
                    `pages.checkout.payment.this${capitalize(stripePriceFull?.recurring?.interval)}`,
                    {
                      count: stripePriceFull?.recurring?.interval_count,
                      price: localizeCurrency({
                        amountInCents: paymentIntent.amount,
                        currency: paymentIntent.currency,
                      }),
                    },
                  )}
                </p>
              )}
            </div>
          )
        )}
      </div>
    </div>
  )
}
